import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getDeliveryInformation, setDeliveryOption } from '../../../utils/API/Delivery/deliveryAPI';
import {
    createAddDeliveryMethodRequestDtoSchema,
    DeliveryLocation,
    DeliveryMethod,
    DeliveryModel,
    DeliveryWindow,
    SetDeliveryOptionResponseModel,
    UpdateDeliveryRequestDto,
} from '../../../utils/API/Delivery/deliveryAPISchema';
import { Log } from '../../../utils/API/Logging/LoggingAPI';
import { FEEDBACK_TEXT } from '../../../utils/validationHelpers';
import { useShoppingCartStore } from '../../shoppingCart/stores/shoppingCartStore';

const emptyErrors = () => ({
  deliveryMethod: [] as string[],
  deliveryWindow: [] as string[],
  deliveryLocation: [] as string[],
});

export const useDeliveryInformationStore = defineStore('deliveryInformation', () => {
  // State
  const deliveryInformation = ref<DeliveryModel | null | undefined>(null);
  const selectedDeliveryMethod = ref<DeliveryMethod | null | undefined>(null);
  const selectedDeliveryLocation = ref<DeliveryLocation | null | undefined>(null);
  const selectedDeliveryWindow = ref<DeliveryWindow | undefined>(undefined);
  const errors = ref(emptyErrors());

  // Getters
  const isDeliveryStepCompleted = computed(() => {
    const method = selectedDeliveryMethod.value;
    if (!method?.id) return false;
    const hasNoDeliveryErrors = Object.values(errors.value).every((e) => e.length === 0);
    if (!hasNoDeliveryErrors) return false;
    const requiresWindow = (method.deliveryWindows?.length ?? 0) > 0;
    const hasValidWindow = !requiresWindow
      ? true
      : Boolean(
          selectedDeliveryWindow.value?.token &&
            method.deliveryWindows?.some((w) => w.token === selectedDeliveryWindow.value?.token)
        );
    if (!hasValidWindow) return false;
    const requiresLocation = (method.deliveryLocations?.length ?? 0) > 0;
    const hasValidLocation = !requiresLocation
      ? true
      : Boolean(
          selectedDeliveryLocation.value?.id &&
            method.deliveryLocations?.some((l) => l.id === selectedDeliveryLocation.value?.id)
        );
    return hasValidWindow && hasValidLocation;
  });

  // Actions
  function setSelectedDeliveryMethod(method: DeliveryMethod | null | undefined) {
    selectedDeliveryMethod.value = method;
    selectedDeliveryLocation.value = method?.deliveryLocations?.[0] ?? undefined;
    selectedDeliveryWindow.value = method?.deliveryWindows?.[0] ?? undefined;
  }
  function setSelectedDeliveryLocation(location: DeliveryLocation | null) {
    selectedDeliveryLocation.value = location;
    if (
      location &&
      selectedDeliveryMethod.value?.deliveryLocations &&
      !selectedDeliveryMethod.value.deliveryLocations.some((l) => l.id === location.id)
    ) {
      selectedDeliveryMethod.value.deliveryLocations.push(location);
    }
  }
  function setSelectedDeliveryWindow(window: DeliveryWindow | undefined) {
    selectedDeliveryWindow.value = window;
  }
  function setError(field: string | number, error: string) {
    errors.value[field] = error;
  }
  function setErrors(e: {
    deliveryMethod: string[];
    deliveryWindow: string[];
    deliveryLocation: string[];
  }) {
    errors.value = e;
  }
  function clearAllErrors() {
    errors.value = emptyErrors();
  }
  function clear() {
    deliveryInformation.value = null;
    selectedDeliveryMethod.value = null;
    selectedDeliveryLocation.value = null;
    selectedDeliveryWindow.value = undefined;
    clearAllErrors();
  }

  async function submitForm(): Promise<boolean> {
    const shoppingCartStore = useShoppingCartStore();

    if (!selectedDeliveryMethod.value?.id) {
      setError('deliveryMethod', FEEDBACK_TEXT().REQUIRED_DELIVERY_METHOD);
      return false;
    }

    const requestBody: UpdateDeliveryRequestDto = {
      deliveryLocationId: selectedDeliveryLocation.value?.id ?? undefined,
    };

    if (selectedDeliveryWindow.value) {
      requestBody.deliveryWindow = selectedDeliveryWindow.value;
    }

    const validationSchema = createAddDeliveryMethodRequestDtoSchema(selectedDeliveryMethod.value);
    if (!validationSchema.safeParse(requestBody).success) return false;

    let data: SetDeliveryOptionResponseModel | null = null;
    try {
      data = await setDeliveryOption(selectedDeliveryMethod.value.id, requestBody);
    } catch (error) {
      if (error instanceof Error) {
        Log({
          LogLevel: 'Warning',
          Message: `[deliveryInformationStore] Failed to update delivery information: ${error.message}`,
        });
      }
    }

    if (!data) return false;

    clearAllErrors();
    shoppingCartStore.setCart(data.cartDto);
    return true;
  }

  async function loadDeliveryInformation(): Promise<void> {
    const previousLocationId = selectedDeliveryLocation.value?.id;
    const previousWindowStart = selectedDeliveryWindow.value?.start;
    const previousWindowEnd = selectedDeliveryWindow.value?.end;

    try {
      const { deliveryDto } = await getDeliveryInformation();
      deliveryInformation.value = deliveryDto;
      const methods = deliveryDto.deliveryMethods ?? [];
      const selectedMethod =
        methods.find((o) => o.selected) ?? methods.find((o) => o.isDefaultSelection) ?? methods[0];
      setSelectedDeliveryMethod(selectedMethod);
      const restoredLocation = selectedMethod?.deliveryLocations?.find(
        (l) => l.id === previousLocationId
      );
      if (restoredLocation) setSelectedDeliveryLocation(restoredLocation);
      const restoredWindow = selectedMethod?.deliveryWindows?.find(
        (w) => w.start === previousWindowStart && w.end === previousWindowEnd
      );
      if (restoredWindow) setSelectedDeliveryWindow(restoredWindow);
    } catch (error) {
      Log({
        LogLevel: 'Warning',
        Message: `[deliveryInformationStore] Failed to load delivery information: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  return {
    deliveryInformation,
    selectedDeliveryMethod,
    selectedDeliveryLocation,
    selectedDeliveryWindow,
    errors,
    isDeliveryStepCompleted,
    setSelectedDeliveryMethod,
    setSelectedDeliveryLocation,
    setSelectedDeliveryWindow,
    setError,
    setErrors,
    clearAllErrors,
    clear,
    submitForm,
    loadDeliveryInformation,
  };
});
